import React, { useEffect, useState, useCallback } from "react";
import { api, money } from "@/lib/api";
import { toast } from "sonner";
import { Timer, Save, Loader2, PackageX, RefreshCw, Clock3, CheckCircle2, Hourglass } from "lucide-react";

const timeLeft = (iso) => {
  if (!iso) return { label: "—", urgent: false, expired: false };
  const ms = new Date(iso).getTime() - Date.now();
  if (ms <= 0) return { label: "Expired", urgent: true, expired: true };
  const h = Math.floor(ms / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  return { label: h > 0 ? `${h}h ${m}m left` : `${m}m left`, urgent: ms < 4 * 3600000, expired: false };
};

const HoldsDashboard = () => {
  const [holds, setHolds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [releasing, setReleasing] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/pos/holds");
      setHolds(r.data.items || []);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    const iv = setInterval(load, 60000);
    return () => clearInterval(iv);
  }, [load]);

  const release = async (o) => {
    if (!window.confirm(`Release this hold and free the stock?${o.deposit_paid ? " The paid deposit is kept as a no-show fee." : ""}`)) return;
    setReleasing(o.order_id);
    try {
      await api.post(`/pos/holds/${o.order_id}/release`);
      toast.success("Hold released — stock freed");
      setHolds((prev) => prev.filter((h) => h.order_id !== o.order_id));
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not release"); }
    setReleasing(null);
  };

  return (
    <div className="mb-8" data-testid="holds-dashboard">
      <div className="flex items-center justify-between mb-3">
        <div className="font-heading text-lg text-brand-navy">Active holds ({holds.length})</div>
        <button onClick={load} data-testid="holds-refresh-btn" className="inline-flex items-center gap-1 text-sm font-bold text-slate-500 hover:text-brand-navy"><RefreshCw className="h-4 w-4" /> Refresh</button>
      </div>
      {loading ? (
        <div className="text-slate-400 text-sm">Loading…</div>
      ) : holds.length === 0 ? (
        <div className="text-center py-10 border-2 border-dashed border-slate-200 rounded-2xl" data-testid="holds-empty">
          <Hourglass className="h-9 w-9 mx-auto text-slate-300" />
          <div className="font-bold text-slate-500 mt-2">No active holds right now</div>
        </div>
      ) : (
        <div className="space-y-2">
          {holds.map((o) => {
            const tl = timeLeft(o.hold_expires_at);
            const depositPaid = o.status === "HELD";
            return (
              <div key={o.order_id} data-testid={`hold-row-${o.order_id}`}
                className="bg-white border border-slate-200 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-heading text-brand-navy">{o.customer_name || "Guest"}</span>
                    <span className={`text-[10px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full inline-flex items-center gap-1 ${depositPaid ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                      {depositPaid ? <><CheckCircle2 className="h-3 w-3" /> Deposit paid</> : <><Clock3 className="h-3 w-3" /> Awaiting deposit</>}
                    </span>
                  </div>
                  <div className="text-xs text-slate-500 truncate">
                    {(o.items || []).map((i) => `${i.quantity}× ${i.product_name}`).join(", ")}
                  </div>
                  <div className="text-xs text-slate-400 mt-0.5">{o.customer_email || "no email"} · {o.order_id.slice(-8).toUpperCase()}</div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-sm"><span className="text-slate-400">Deposit</span> <b>{money(o.deposit_amount)}</b> · <span className="text-slate-400">Balance</span> <b>{money(o.balance_due)}</b></div>
                  <div className={`text-xs font-bold ${tl.urgent ? "text-brand-red" : "text-slate-500"}`} data-testid={`hold-timeleft-${o.order_id}`}>{tl.label}</div>
                </div>
                <button onClick={() => release(o)} disabled={releasing === o.order_id} data-testid={`hold-release-${o.order_id}`}
                  className="inline-flex items-center justify-center gap-1 bg-brand-red/10 text-brand-red hover:bg-brand-red hover:text-white font-bold rounded-full h-9 px-4 shrink-0 transition disabled:opacity-60">
                  {releasing === o.order_id ? <Loader2 className="h-4 w-4 animate-spin" /> : <PackageX className="h-4 w-4" />} Release
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export const HoldsTab = () => {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings").then((r) => setCfg({
      hold_enabled: r.data.hold_enabled !== false,
      hold_deposit_amount: Number(r.data.hold_deposit_amount ?? 2),
      hold_min_price: Number(r.data.hold_min_price ?? 50),
    }));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings", {
        hold_enabled: cfg.hold_enabled,
        hold_deposit_amount: Number(cfg.hold_deposit_amount) || 0,
        hold_min_price: Number(cfg.hold_min_price) || 0,
      });
      toast.success("Hold settings saved");
    } catch { toast.error("Could not save"); }
    setSaving(false);
  };

  if (!cfg) return <div className="text-slate-400">Loading…</div>;

  return (
    <div data-testid="holds-tab" className="max-w-xl">
      <div className="flex items-center gap-2 mb-1">
        <Timer className="h-6 w-6 text-brand-red" />
        <div className="font-heading text-2xl text-brand-navy">Holds & Deposits</div>
      </div>
      <p className="text-slate-500 text-sm mb-5">
        Shoppers can reserve an eligible item for 24 hours by paying a small refundable deposit (credited
        toward the price). If the balance isn't paid in time, the item is released and the deposit is kept
        as a no-show fee — a reminder email is sent first.
      </p>

      <HoldsDashboard />

      <div className="font-heading text-lg text-brand-navy mb-3">Settings</div>
      <div className="bg-white border border-slate-200 rounded-2xl p-5 space-y-4">
        <label className="flex items-center justify-between">
          <span className="font-bold text-slate-700">Enable 24-hour holds</span>
          <input type="checkbox" checked={cfg.hold_enabled} data-testid="holds-enabled"
            onChange={(e) => setCfg({ ...cfg, hold_enabled: e.target.checked })} className="h-5 w-5" />
        </label>
        <label className="block">
          <span className="text-xs font-bold text-slate-600 uppercase tracking-wider">Deposit per item (CAD)</span>
          <input type="number" min="0" step="0.5" value={cfg.hold_deposit_amount} data-testid="holds-deposit"
            onChange={(e) => setCfg({ ...cfg, hold_deposit_amount: e.target.value })}
            className="mt-1 w-full h-11 rounded-lg border border-slate-200 px-3" />
        </label>
        <label className="block">
          <span className="text-xs font-bold text-slate-600 uppercase tracking-wider">Minimum item price to allow a hold (CAD)</span>
          <input type="number" min="0" step="1" value={cfg.hold_min_price} data-testid="holds-minprice"
            onChange={(e) => setCfg({ ...cfg, hold_min_price: e.target.value })}
            className="mt-1 w-full h-11 rounded-lg border border-slate-200 px-3" />
          <span className="text-xs text-slate-400">Only items at or above this price show a Hold button.</span>
        </label>
        <button onClick={save} disabled={saving} data-testid="holds-save-btn"
          className="inline-flex items-center gap-2 bg-brand-navy text-white rounded-full h-10 px-5 font-bold active:scale-95 disabled:opacity-60">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save
        </button>
      </div>
    </div>
  );
};
